import React, { useState, useCallback, useEffect } from 'react';

import dashboardApi from 'services/apis/dashboard';

import { useSnackbar } from 'v3/components/Snackbar';
import moment from 'moment';
import { Table, Text, Link, Button } from 'v3/components';
import { useQuery } from 'hooks';
import { formatDateTime } from 'v3/utils/formatter';
import Card, { Number, Description, SeeAll } from './Card';

export default function NegotiatorControl({ dashboard, name, id }) {
  const [isFetching, setFetching] = useState(false);
  const [data, setData] = useState({});
  const [filter, setFilter] = useState({});
  const [currentPage, setCurrentPage] = useState({});
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(false);
  const [daysFromNow, setDaysFromNow] = useState(3);
  const snackbar = useSnackbar();

  const queryParams = useQuery();


  const checklistColumnName = (value) => {
    if (value === null) {
      return "N/A"
    } else if (value === 1) {
      return "Respondido"
    } else {
      return "Pendente"
    }
  }

  const registerColumnStatus = (value) => {
    if (value === "REGISTER_APPROVAL") {
      return "• Aprovado"
    } else if (value === "REGISTER_ANALYSIS") {
      return "• Análise"
    } else if (value === "REGISTER_DECLINE") {
      return "• Reprovado"
    }
  }

  const grColumnStatus = (value) => {
    if (value === "GR_APPROVAL") {
      return "• Aprovado"
    } else if (value === "GR_ANALYSIS") {
      return "• Análise"
    } else if (value === "GR_DECLINE") {
      return "• Reprovado"
    }
  }

  const checklistColumnColor = (value) => {
    if (value === null) {
      return "gray"
    } else if (value === 1) {
      return "green"
    } else {
      return "red"
    }
  }

  useEffect(() => {
    setDaysFromNow(moment(new Date()).diff(filter.start_date, 'days'));
  }, [filter.start_date]);

  function setQueryParams(filters) {
    const { tags = [], start_date, end_date } = filters;

    queryParams.delete('tags');

    if (tags) {
      tags.forEach(tag => {
        queryParams.append('tags', tag.id);
      });
    }

    if (start_date) {
      queryParams.set('start_date', start_date);
    }
    if (end_date) {
      queryParams.set('end_date', end_date);
    }

    if (tags?.length === 0) {
      queryParams.set('no-tags', true);
    } else {
      queryParams.delete('no-tags');
    }
  }

  const fetchTravels = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      const {
        tags = [],
        clients = [],
        negotiators = [],
        costCenters = [],
        recuseCostCenters = [],
        pcps = [],
        companies = [],
        page = 1,
        start_date,
        end_date,
      } = filters;

      if (page === 1) {
        setFetching(true);
      }

      setQueryParams(filters);
      setFilter(filters);
      setCurrentPage(+page);

      const response = await dashboardApi.get(`/negotiator-control`, {
        params: {
          tags: tags?.map(tag => tag.id),
          clients: clients?.map(client => client.id),
          start_date,
          negotiators: negotiators?.map(negotiator => negotiator?.id),
          costCenters: costCenters?.map(costCenter => costCenter?.id),
          recuseCostCenters: recuseCostCenters?.map(
            costCenters => costCenters?.id
          ),
          pcps: pcps?.map(pcp => pcp?.id),
          companies: companies?.map(company => company?.id),
          end_date: end_date || undefined,
          page,
        },
      });

      setPagination(response.data?.last?.pagination);

      if (page === 1) {
        setData(response.data);
      } else {
        setData(oldData => ({
          ...oldData,
          last: {
            ...oldData.last,
            data: [...oldData.last.data, ...response.data.last.data],
          },
        }));
      }
    } catch (ex) {
      snackbar.show(<Text>Erro ao carregar últimas viagens</Text>, {
        type: 'error',
      });
    } finally {
      setFetching(false);
      setLoading(false);
    }
  }, []);
  return (
    <Card
      title="Viagens negociadores"
      isLoading={isFetching}
      fetchFn={fetchTravels}
      onFiltersChanged={fetchTravels}
      dashboard={dashboard}
      name={name}
      excludeFilterByBranchOffice
      excludeFilterByRecuseCostCenters={false}
      id={id}
      ListComponent={
        <>
          <Table striped>
            <thead>
              <tr>
                <th className="text-nowrap">
                  <Text weight="500" color="dark">
                    Troca de Nota
                  </Text>
                </th>
                <th>
                  <Text weight="500" color="dark">
                    Centro de custo
                  </Text>
                </th>
                <th>
                  <Text weight="500" color="dark">
                    Empresa
                  </Text>
                </th>
                <th>
                  <Text weight="500" color="dark">
                    Tags da carga
                  </Text>
                </th>
                <th>
                  <Text weight="500" color="dark">
                    Nº Pedido Cliente
                  </Text>
                </th>
                <th>
                  <Text weight="500" color="dark">
                    Viagem
                  </Text>
                </th>
                <th>
                  <Text weight="500" color="dark">
                    Motorista
                  </Text>
                </th>
                <th>
                  <Text weight="500" color="dark">
                    Veículo
                  </Text>
                </th>
                <th>
                  <Text weight="500" color="dark">
                    Cliente
                  </Text>
                </th>
                <th>
                  <Text weight="500" color="dark">
                    Carregamento
                  </Text>
                </th>
                <th>
                  <Text weight="500" color="dark">
                    Checklist veículo
                  </Text>
                </th>
                <th>
                  <Text weight="500" color="dark">
                    Responsável cadastro
                  </Text>
                </th>
                <th>
                  <Text weight="500" color="dark">
                    Responsável GR
                  </Text>
                </th>
                <th>
                  <Text weight="500" color="dark">
                    Origem
                  </Text>
                </th>
                <th>
                  <Text weight="500" color="dark">
                    Destino
                  </Text>
                </th>
                <th>
                  <Text weight="500" color="dark">
                    Negociador
                  </Text>
                </th>
              </tr>
            </thead>
            <tbody>
              {data.last?.data?.map(travel => {
                const wagonsFormat = travel?.wagons
                  ? travel?.wagons?.split(",").map(wagons => {
                    const [id, plate] = wagons.trim().split(" - ");
                    return { id, plate };
                  })
                  : [];

                return (
                  <tr>
                    <td>
                      <Text type="regular" color="gray">
                        {travel?.replacement_order
                          ? `${travel?.replacement_order}º Troca Nota`
                          : 'Viagem Normal'}
                      </Text>
                    </td>
                    <td>
                      <Text type="regular" color="gray">
                        {travel?.cost_center || '-'}
                      </Text>
                    </td>
                    <td>
                      <Text type="regular" color="gray">
                        {travel?.company_name || '-'}
                      </Text>
                    </td>
                    <td>
                      <Text type="regular" color="gray">
                        {travel?.load_tags || '-'}
                      </Text>
                    </td>
                    <td>
                      <Text type="regular" color="gray">
                        {travel?.client_order_number || '-'}
                      </Text>
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                        <Link
                          to={`/viagens/${travel?.attended_id}`}
                          target="_blank"
                        >
                          {travel?.attended_id}
                        </Link>
                        <Text type="regular" color="gray">
                          {travel?.travel_status}
                        </Text>
                      </div>
                    </td>
                    <td>
                      <Link
                        to={`/motoristas/${travel?.driver_id}`}
                        target="_blank"
                      >
                        {travel?.driver_name}
                      </Link>
                    </td>
                    <td>
                      <Link
                        to={`/veiculos/${travel?.vehicle_id}`}
                        target="_blank"
                      >
                        {travel?.plate}
                      </Link>
                      {wagonsFormat.map((wagon) => (
                        <Link key={wagon?.id || wagon?.plate} to={`/implementos/${wagon?.id}`} target="_blank">
                          {wagon?.plate}
                        </Link>
                      ))}
                    </td>
                    <td>
                      <Link
                        to={`/clientes/${travel?.client_id}`}
                        target="_blank"
                      >
                        {travel?.client_name}
                      </Link>
                    </td>
                    {
                      travel?.total_origins && (
                        <td>
                          <div style={{
                            minWidth: '200px',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '0.25rem',
                            flexDirection: 'column'
                          }}>
                            <Text type="regular" color="gray" weight={500}>
                              {travel?.total_origins} Coletas
                            </Text>
                          </div>

                          <div>
                            <Text type="regular" color="red" weight={500}>
                              • {travel?.pending_origins} Pendentes(s)
                            </Text>
                          </div>

                          <div>
                            <Text type="regular" color="green" weight={500}>
                              • {travel?.completed_origins} Concluídas(s)
                            </Text>
                          </div>
                        </td>
                      )
                    }
                    <td>
                      <Text type="regular" weight={500} color={checklistColumnColor(travel?.checklist_answered)}>
                        {checklistColumnName(travel?.checklist_answered)}
                      </Text>
                    </td>
                    <td>
                      <div>
                        <Text type="regular" weight={500} color="gray">
                          {registerColumnStatus(travel?.validate_status_approval)}
                        </Text>
                      </div>
                      <div>
                        <Text type="regular" weight={400} color="gray">
                          {travel?.validate_user}
                        </Text>
                      </div>
                      <div>
                        <Text type="small" color="gray">
                          {formatDateTime(travel?.validate_created_at)}
                        </Text>
                      </div>
                    </td>
                    <td>
                      <div>
                        <Text type="regular" weight={500} color="gray">
                          {grColumnStatus(travel?.validate_gr_event)}
                        </Text>
                      </div>
                      <div>
                        <Text type="regular" weight={400} color="gray">
                          {travel?.validate_gr_user}
                        </Text>
                      </div>
                      <div>
                        <Text type="small" color="gray">
                          {formatDateTime(travel?.validate_gr_created_at)}
                        </Text>
                      </div>
                    </td>
                    <td>
                      <Text type="regular" color="gray" truncate>
                        {travel?.city_origin} - {travel?.uf_origin}
                      </Text>
                    </td>
                    <td>
                      <Text type="regular" color="gray" truncate>
                        {travel?.city_destination} - {travel?.uf_destination}
                      </Text>
                    </td>
                    <td>
                      <Link
                        to={`/usuarios/${travel?.negotiator_id}`}
                        target="_blank"
                      >
                        {travel?.negotiator_name}
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          {pagination?.lastPage > 1 && pagination?.to < pagination?.total && (
            <div className="text-center mt-3 col-12">
              <Button
                className="mt-2 ml-auto align-item-center"
                onClick={() => {
                  fetchTravels({
                    page: currentPage + 1,
                    tags: filter?.tags,
                    clients: filter?.clients,
                    start_date: filter?.start_date,
                    end_date: filter?.end_date,
                    costCenters: filter?.costCenters,
                    recuseCostCenters: filter?.recuseCostCenters,
                    pcps: filter?.pcps,
                    negotiators: filter?.negotiators,
                    companies: filter?.companies,
                  });
                }}
                loading={loading}
              >
                <Text type="regular" color="white" truncate>
                  Ver mais
                </Text>
              </Button>
            </div>
          )}
          {!!data.total && (
            <SeeAll
              to={{
                pathname: '/viagens',
                search: queryParams.toString(),
              }}
            >
              ver todas as {data.total} viagens
            </SeeAll>
          )}
        </>
      }
      NumericComponent={
        <div className="d-flex justify-content-center text-center flex-column">
          <Number color="warning">{data.total}</Number>
          <Description>
            {daysFromNow > 1 && daysFromNow < 61
              ? ` nos últimos ${daysFromNow} dias`
              : ` nos últimos ${filter.start_date} dias`}
          </Description>
        </div>
      }
    />
  );
}
